import { localeToLanguage, normalizeLocale } from "./localeResolution.js";
import { normalizeDisplayCurrency } from "./currencyResolution.js";
import { getManifestI18n, getManifestMarket, localeToUrlSegment } from "./manifestI18n.js";
import { getMarketProfileForManifest } from "./marketProfiles.js";
import { resolveCurrentFxRate } from "../utils/automap/buyerFxHistoryFormatters.js";
import { isConvertedDisplayCurrency } from "./fxDisplayCopy.js";

let activeContext = {};

export function setIntlAnalyticsContext(next = {}) {
  activeContext = { ...next };
}

export function getIntlAnalyticsContext() {
  return { ...activeContext };
}

/**
 * Build analytics dimensions for page views and conversion events.
 */
export function buildIntlAnalyticsContext({
  locale,
  displayCurrency,
  manifest = null,
  urlLocale = null,
  clientViewSlug = null,
  surface = "clientView",
  ratesMeta = null,
  rates = null,
} = {}) {
  const manifestI18n = getManifestI18n(manifest);
  const market = getManifestMarket(manifest);
  const profile = manifest ? getMarketProfileForManifest(manifest) : null;
  const normalizedLocale = normalizeLocale(locale || manifestI18n.defaultLocale);
  const routeLocale = urlLocale ? localeToUrlSegment(urlLocale, manifestI18n) : localeToUrlSegment(normalizedLocale, manifestI18n);
  const normalizedCurrency = normalizeDisplayCurrency(displayCurrency || manifestI18n.defaultCurrency);
  const convertedDisplay = isConvertedDisplayCurrency(normalizedCurrency, market.priceStorageCurrency || profile?.priceStorageCurrency || manifestI18n.defaultCurrency);
  const fxDisplayRate = convertedDisplay
    ? resolveCurrentFxRate({ displayCurrency: normalizedCurrency, rates })
    : null;

  return {
    locale: normalizedLocale,
    language: localeToLanguage(normalizedLocale),
    displayCurrency: normalizedCurrency,
    clientViewSlug: clientViewSlug || null,
    surface,
    routeLocale: routeLocale || null,
    manifestDefaultLocale: manifestI18n.defaultLocale,
    manifestSupportedLocales: manifestI18n.supportedLocales,
    fallbackLocale: manifestI18n.fallbackLocale || manifestI18n.defaultLocale,
    marketCode: market.code,
    marketCountry: market.country || profile?.country || null,
    priceStorageCurrency: market.priceStorageCurrency || profile?.priceStorageCurrency || manifestI18n.defaultCurrency,
    resolvedLocale: normalizedLocale,
    convertedDisplay,
    fxStale: Boolean(ratesMeta?.stale),
    fxSource: ratesMeta?.source || null,
    fxIsFallback: Boolean(ratesMeta?.isFallback),
    fxSnapshotId: ratesMeta?.snapshotId ?? null,
    fxEffectiveDate: ratesMeta?.effectiveDate || ratesMeta?.asOf || null,
    fxDisplayRate,
  };
}

export function mergeIntlAnalyticsPayload(payload = {}) {
  const ctx = getIntlAnalyticsContext();
  if (!Object.keys(ctx).length) {
    return payload;
  }
  return {
    ...ctx,
    ...payload,
    locale: payload.locale || ctx.locale,
    language: payload.language || ctx.language,
    displayCurrency: payload.displayCurrency || ctx.displayCurrency,
  };
}
